import React, { useState } from 'react';
import { Icon } from '../Icon';

interface ContactPageProps {
  onBack: () => void;
}

export const ContactPage: React.FC<ContactPageProps> = ({ onBack }) => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const faqs = [
    { q: "As calculadoras são gratuitas?", a: "Sim! Todas as calculadoras do Calculadora PRO são completamente gratuitas e não exigem cadastro." },
    { q: "Meus dados estão seguros?", a: "Sim. Não coletamos dados pessoais. Todos os cálculos são feitos localmente no seu navegador." },
    { q: "Funciona no celular?", a: "Sim! Nosso site é totalmente responsivo e funciona perfeitamente em celulares, tablets e computadores." },
    { q: "Como posso solicitar uma nova calculadora?", a: "Use nosso formulário de contato com o assunto 'Sugestão de Calculadora' e descreva a ferramenta que você gostaria de ver." },
    { q: "Encontrei um erro. Como reportar?", a: "Por favor, use o formulário de contato descrevendo o erro, qual calculadora está usando e os valores inseridos." }
  ];

  return (
    <div className="min-h-screen bg-theme-bg flex flex-col font-inter transition-colors duration-300">
      <nav className="bg-theme-card border-b border-theme-border px-6 py-4 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={onBack}
            className="group flex items-center gap-2.5 text-base font-bold text-theme-muted hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <Icon
              name="arrow-left"
              className="w-5 h-5 transform group-hover:-translate-x-1.5 transition-transform"
            />
            Voltar ao Hub
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black px-4 py-2 rounded-xl uppercase tracking-widest shadow-sm bg-indigo-50 text-indigo-600 dark:bg-slate-800 dark:text-indigo-400">
              Contato
            </span>
          </div>
        </div>
      </nav>

      <main className="flex-grow max-w-4xl mx-auto w-full px-6 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-[1000] text-theme-text mb-4 tracking-tight">Estamos aqui para ajudar!</h1>
          <p className="text-theme-muted font-medium text-lg leading-relaxed max-w-2xl mx-auto">
            Envie sua mensagem ou tire suas dúvidas rapidamente com a equipe do Calculadora PRO.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-theme-card p-6 rounded-3xl border border-theme-border text-center shadow-sm">
            <div className="w-12 h-12 bg-indigo-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl">
              📧
            </div>
            <h3 className="text-lg font-bold text-theme-text mb-1">E-mail</h3>
            <p className="text-theme-muted text-sm">jurusfacil@gmail.com</p>
            <p className="text-theme-muted text-xs mt-2">Resposta em até 24h</p>
          </div>
          <div className="bg-theme-card p-6 rounded-3xl border border-theme-border text-center shadow-sm">
            <div className="w-12 h-12 bg-emerald-50 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl">
              🌐
            </div>
            <h3 className="text-lg font-bold text-theme-text mb-1">Site</h3>
            <p className="text-theme-muted text-sm">www.calculadora.dev.br</p>
            <p className="text-theme-muted text-xs mt-2">Disponível 24/7</p>
          </div>
          <div className="bg-theme-card p-6 rounded-3xl border border-theme-border text-center shadow-sm">
            <div className="w-12 h-12 bg-amber-50 dark:bg-slate-800 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl">
              💬
            </div>
            <h3 className="text-lg font-bold text-theme-text mb-1">Feedback</h3>
            <p className="text-theme-muted text-sm">Sugestões bem-vindas</p>
            <p className="text-theme-muted text-xs mt-2">Ajude-nos a melhorar</p>
          </div>
        </div>

        <div className="bg-theme-card p-8 md:p-10 rounded-3xl border border-theme-border shadow-sm mb-12">
          <h2 className="text-2xl font-[1000] text-theme-text mb-8">Envie sua mensagem</h2>

          {submitted ? (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/30 text-emerald-700 dark:text-emerald-400 p-6 rounded-2xl text-center animate-in fade-in">
              <h3 className="text-xl font-bold mb-2">Mensagem enviada com sucesso!</h3>
              <p>Obrigado por entrar em contato. Responderemos em breve.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-theme-text mb-2">Nome *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3 text-theme-text focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-theme-text mb-2">E-mail *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3 text-theme-text focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-theme-text mb-2">Assunto *</label>
                <input
                  type="text"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3 text-theme-text focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                  placeholder="Sobre o que você quer falar?"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-theme-text mb-2">Mensagem *</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl px-4 py-3 text-theme-text focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all resize-none"
                  placeholder="Descreva sua dúvida, sugestão ou problema..."
                />
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-accent-gradient text-white rounded-xl font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.01] active:scale-95 transition-all uppercase tracking-wider"
              >
                Enviar Mensagem
              </button>
            </form>
          )}
        </div>

        <div className="mb-12">
          <h2 className="text-2xl font-[1000] text-theme-text mb-6">Perguntas Frequentes</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-theme-card border border-theme-border rounded-2xl overflow-hidden transition-all duration-300"
              >
                <button
                  className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none"
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                >
                  <span className="font-bold text-theme-text">{faq.q}</span>
                  <span className={`text-indigo-500 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}>
                    <Icon name="chevron-down" className="w-5 h-5" />
                  </span>
                </button>
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${activeFaq === index ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-theme-muted">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="py-12 border-t border-theme-border text-center mt-auto">
        <p className="text-[10px] font-black text-theme-muted uppercase tracking-[0.3em]">
          Calculadora PRO • Ferramentas Úteis v4.0
        </p>
      </footer>
    </div>
  );
};
